/**
 * The 3D scene: renderer, render loop and store subscriptions (S1-09 … S1-14).
 *
 * Every frame: advance playback → interpolate the scale profile → evaluate the world (float64) → camera rig →
 * write positions relative to the focus point (floating origin) → render stars, scene and labels.
 * The scene reads stores and writes them only through store actions.
 */
import * as THREE from 'three';
import { get } from 'svelte/store';
import { CSS2DRenderer } from 'three/addons/renderers/CSS2DRenderer.js';
import type { BodyData, LocalizedText, MissionsFile, OrbitsFile, StarsFile, TrajectoryData } from '../core/data';
import {
  OVERVIEW,
  REALISTIC,
  type ScaleMode,
  type ScaleProfile,
  easeInOut,
  interpolateProfile,
} from '../core/scaleProfiles';
import { smoothingFactor } from '../core/easing';
import { nearestFlyby } from '../core/slowmo';
import { type MoonBand, Trajectory, createState } from '../core/trajectory';
import { lifeState, silentFrom } from '../core/missionState';
import type { BodyTable } from '../core/bodyEphemeris';
import { QUALITY_TIERS, type Quality, QualityMonitor, TIER_MAX_DPR } from '../core/quality';
import { type Telemetry, computeTelemetry } from '../core/telemetry';
import { parseUtc, utcMsToTdbDays } from '../core/time';
import { AU_KM } from '../core/units';
import { type Vec3, length, vec3 } from '../core/vec3';
import { type Lang, lang, translate } from '../i18n';
import { advancePlayback, effectiveSpeed } from '../state/playback';
import { cinematicCamera, motionReduced, qualityPref } from '../state/settings';
import {
  currentDate,
  flybyTimes,
  focus,
  refocus,
  focusContext,
  focusOn,
  follow,
  followTarget,
  fps as fpsStore,
  playback,
  scaleMode,
  selectedMission,
  setDate,
} from '../state/stores';
import { Bodies, type ViewInfo } from './bodies';
import { CameraRig } from './cameraRig';
import { type Label, createLabel, declutter, setLabelText } from './labels';
import { type Pickable, pick } from './picking';
import { SpacecraftSprites } from './spacecraft';
import { PostFx } from './postfx';
import { createMilkyWay } from './sky';
import { createCatalogStars, createStarfield } from './starfield';
import { diamondTexture } from './textures';
import {
  type Highlight,
  type LineFrame,
  TrajectoryLine,
  beginBetweenFrames,
  beginFrame,
  precomputedThisFrame,
} from './trajectoryLine';
import { createRibbonGeometry, createRibbonMaterial, createRibbonUniforms } from './ribbonMaterial';
import { World } from './world';

/** Duration of the animated scale-mode switch (S1-14). */
const MODE_SWITCH_SECONDS = 2.5;
const FOV = 50;
const DOUBLE_TAP_MS = 350;
/** A tap moves less than this (px) and lasts at most TAP_MAX_MS. */
const TAP_SLOP_PX = 6;
const TAP_MAX_MS = 500;
/** Two taps of a double tap on empty space lie within this distance (px). */
const DOUBLE_TAP_SLOP_PX = 30;
/** Zoom steps (S2-06): double tap on empty space zooms in, a two-finger tap zooms out. */
const ZOOM_IN_STEP = 0.45;
/** Cinematic framing (S2-11): active within ±2 days of a flyby; frames planet + spacecraft with this margin. */
const CINE_WINDOW_MS = 2 * 86_400_000;
const CINE_MARGIN = 1.25;
const CINE_TAU_S = 0.8;
/**
 * Seconds after the first frame before the post-processing chain loads and the HDR shader variants compile, in small
 * pieces (S4-03): for the start tier if it is post-processed, else for a later tier change.
 */
const HDR_DELAY_S = 1;
const HDR_DELAY_LOW_S = 4;
/**
 * Wall-clock time one task may spend compiling shaders, ms (S4-03 part 6): Lighthouse counts every task above 50 ms at
 * 4× CPU as long, so a slice stays well below 12.5 ms on a fast machine and 50 ms on a phone.
 */
const COMPILE_SLICE_MS = 8;
/** Drawables per warm-up draw call (see warmDraw): each draw call walks the whole scene. */
const WARM_DRAW_BATCH = 8;

/** The textures a drawable's materials hold (maps and shader uniforms) that have their image already. */
function texturesOf(object: THREE.Object3D): THREE.Texture[] {
  const material = (object as Partial<THREE.Mesh>).material;
  const out: THREE.Texture[] = [];
  const add = (v: unknown): void => {
    const t = v as Partial<THREE.Texture> | null;
    if (!t?.isTexture || !t.image) return;
    if ((t.image as { complete?: boolean }).complete === false) return;
    out.push(t as THREE.Texture);
  };
  for (const m of Array.isArray(material) ? material : material ? [material] : []) {
    for (const v of Object.values(m)) add(v);
    for (const u of Object.values((m as Partial<THREE.ShaderMaterial>).uniforms ?? {})) add(u.value);
  }
  return out;
}

/** Lets the browser run other tasks (input, rendering) before the caller continues: a task boundary. */
export function yieldToMain(): Promise<void> {
  const s = (globalThis as { scheduler?: { yield?: () => Promise<void> } }).scheduler;
  return s?.yield ? s.yield() : new Promise((resolve) => setTimeout(resolve, 0));
}

/** Intro (S2-12): Sun ignition and camera sweep durations. */
const INTRO_IGNITION_S = 1.2;
const INTRO_SWEEP_S = 4.8;
/**
 * Dark hold before the sweep: the post-processing tier switches on, textures and the star catalogue go to the GPU and
 * the trajectory precomputation runs while the screen is still dark, so no hitch lands in the camera move
 * (maintainer feedback on Sprint 2 and on the Sprint 3 start). The sweep starts as soon as all of that is done, after
 * at least INTRO_HOLD_MIN_S and at most INTRO_HOLD_MAX_S.
 */
/** Framing of a landed spacecraft: the body it landed on, this many body radii across. */
const LANDED_FRAMING = 3;
const INTRO_HOLD_MIN_S = 0.4;
const INTRO_HOLD_MAX_S = 3.5;
/**
 * Once the scene is warm, the dark hold measures the frame time for this long; above INTRO_BENCH_BUDGET_MS (median)
 * it drops a tier and measures again — in the dark, before the sweep. The quality monitor itself stays off during
 * the intro (a tier switch would hitch the sweep), so a weak GPU used to run the whole sweep at the high tier
 * (maintainer feedback: "the intro still looks choppy").
 */
const INTRO_BENCH_S = 0.5;
/**
 * Trail sampling budget inside a frame, ms (S4-03 part 6): enough for what playback reveals; the bulk runs in a task
 * between frames (sampleBetweenFrames), which the intro sweep and a camera drag skip.
 */
const FRAME_SAMPLE_BUDGET_MS = 2;
/**
 * Trail sampling budget of the task between frames while the scene is at rest (paused, no flight, no mode morph), ms:
 * phones too, so the trails of the start appear within a few seconds (S4-15: at 4 ms they took up to 6 s on a phone).
 */
const REST_SAMPLE_BUDGET_MS = 8;
/** … and while time plays or the camera flies, unless the options set it (phones 4, S4-15). */
const MOVING_SAMPLE_BUDGET_MS = 8;
const INTRO_BENCH_BUDGET_MS = 22;
const ZOOM_OUT_STEP = 2.2;
/** A bubble must be this large on screen before the objects inside it become pickable. */
const MIN_BUBBLE_PICK_PX = 40;
/** Label id suffix of a landing site marker (S4-11): `eagle:site`. */
const SITE_SUFFIX = ':site';

export interface SceneData {
  bodies: BodyData[];
  missions: MissionsFile;
  /** Trajectories available at start; more arrive through addTrajectory (S3-03). */
  trajectories: Map<string, TrajectoryData>;
  /** HORIZONS body tables available at start (S3-06). */
  tables?: Map<string, BodyTable>;
  /** Precomputed line sample times of the start trajectories (from the loader's worker). */
  lineTimes?: Map<string, Float64Array>;
}

export interface SceneOptions {
  canvas: HTMLCanvasElement;
  labelLayer: HTMLElement;
  data: SceneData;
  /** Maximum device pixel ratio (decision: 2, lower on weak devices). */
  maxDpr?: number;
  /** Tier automatic quality starts in (S4-15: phones 'medium'). */
  startTier?: Quality;
  /**
   * Trail sampling budget between frames while time plays or the camera flies, ms (default 8; phones 4, S4-15).
   */
  sampleBudgetMs?: number;
}

/** Live-tunable settings (debug panel). */
export interface SceneSettings {
  overview: ScaleProfile;
  realistic: ScaleProfile;
  lineFrame: LineFrame;
}

export class ZeitflugScene {
  readonly renderer: THREE.WebGLRenderer;
  readonly world: World;
  readonly settings: SceneSettings = {
    overview: { ...OVERVIEW },
    realistic: { ...REALISTIC },
    lineFrame: 'body-relative',
  };
  private readonly labelRenderer: CSS2DRenderer;
  private readonly scene = new THREE.Scene();
  /** Created in populate(), like the camera rig (S4-03 part 6: the constructor was a 90 ms task at 4× CPU). */
  private stars!: THREE.Scene;
  private postfx!: PostFx;
  private milkyWay!: THREE.Mesh;
  /** Automatic quality (D5): the monitor only lowers the tier while the preference is 'auto'. */
  private readonly qualityMonitor: QualityMonitor;
  private qualityAuto = true;
  private requestedTier: Quality;
  private warm = false;
  private warmupTimer: ReturnType<typeof setTimeout> | undefined;
  private readonly camera: THREE.PerspectiveCamera;
  private readonly starCamera: THREE.PerspectiveCamera;
  private rig!: CameraRig;
  /** Created in populate() (S4-03: the constructor in steps). */
  private bodies!: Bodies;
  private sprites!: SpacecraftSprites;
  private readonly lines: TrajectoryLine[] = [];
  private readonly labels: Label[] = [];
  private readonly unsubscribers: (() => void)[] = [];
  /** prefers-reduced-motion or the user's "reduce effects" setting (D9, S2-16). */
  private motionReduced = false;
  private cinematicOn = true;
  private readonly profile: ScaleProfile = { ...OVERVIEW };
  private profileKey = '';
  private profileVersion = 0;
  /** 0 = Overview, 1 = Realistic; animated towards `mixTarget`. */
  private mix = 0;
  private mixTarget = 0;
  private dateMs = 0;
  private focusId = 'sun';
  private followId: string | null = null;
  /** For a focused (not followed) spacecraft: anchor in true space, so the view stays put while time runs. */
  private anchor: { body: string | null; q: Vec3 } | null = null;
  private pendingFlight = true;
  /** False until the first frame: initial mode and focus apply instantly. */
  private started = false;
  private texturesRequested = false;
  private firstFrameMeasured = false;
  private lastRefLength = Number.NaN;
  private lastContext: string | null = null;
  private width = 1;
  private height = 1;
  private dpr = 1;
  private readonly maxDpr: number;
  private lastTime = 0;
  private fpsFrames = 0;
  private cpuMs = 0;
  private cpuMaxMs = 0;
  private fpsTime = 0;
  private lastTap = { id: '', time: 0 };
  private lastEmptyTap = { x: 0, y: 0, time: -Infinity };
  /** Active pointers on the canvas (taps, double taps, two-finger taps). */
  private readonly pointers = new Map<number, { x: number; y: number; time: number; moved: boolean }>();
  /** The current gesture: from the first pointer down to the last pointer up. */
  private gesture: { maxPointers: number; start: number; moved?: boolean } | null = null;
  private readonly focusPoint = vec3();
  private readonly tmp = vec3();
  private readonly tmpV3 = new THREE.Vector3();
  private readonly probeState = createState();
  /** Seconds since the intro started (NaN = no intro running). */
  private introTime = Number.NaN;
  private introSweepPending = false;
  /** Intro waiting in the dark hold: seconds held so far and the callback that reports the start of the sweep. */
  private introHold: {
    held: number;
    ready: boolean;
    started: () => void;
    bench: { time: number; samples: number[] };
  } | null = null;
  /** Flyby times (UTC ms) for cinematic framing (S2-11). */
  private flybys: number[] = [];
  /**
   * Cinematic flyby framing (S2-11): while following a spacecraft through a flyby, the camera eases out to keep the
   * planet and the spacecraft in view, then back to where it was. Any user input hands control back for that flyby.
   */
  private cine: { flyby: number; restoreDist: number; inputMark: number; done: boolean } | null = null;
  /** Screen space covered by the HUD at the top and bottom (px): target values and the eased values in use. */
  private readonly insetsTarget = { top: 0, bottom: 0, right: 0 };
  private readonly insets = { top: 0, bottom: 0, right: 0 };

  constructor(private readonly options: SceneOptions) {
    const { canvas, labelLayer, data } = options;
    this.maxDpr = options.maxDpr ?? 2;
    this.requestedTier = options.startTier ?? 'high';
    this.sampleBudgetMs = options.sampleBudgetMs;
    this.qualityMonitor = new QualityMonitor(this.requestedTier);
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true, logarithmicDepthBuffer: true });
    this.renderer.autoClear = false;
    // in production, three would query every program's info log on first use, which waits for the (parallel) compile
    // to finish and stalls the first frames (S3-02); shader errors show up in development and in the e2e tests' dev
    // checks
    this.renderer.debug.checkShaderErrors = import.meta.env.DEV;
    // count draw calls over the whole frame (stars, scene and post-processing passes), reset once per frame
    this.renderer.info.autoReset = false;
    this.renderer.setClearColor(0x000000, 1);

    this.labelRenderer = new CSS2DRenderer({ element: labelLayer });
    // the labels are buttons above the canvas: a wheel turned over one (or over one that slides under the pointer
    // while zooming) must still zoom (maintainer feedback, Sprint 3)
    labelLayer.addEventListener(
      'wheel',
      (e) => {
        e.preventDefault();
        canvas.dispatchEvent(
          new WheelEvent('wheel', {
            deltaX: e.deltaX,
            deltaY: e.deltaY,
            deltaZ: e.deltaZ,
            deltaMode: e.deltaMode,
            clientX: e.clientX,
            clientY: e.clientY,
            screenX: e.screenX,
            screenY: e.screenY,
            ctrlKey: e.ctrlKey,
            shiftKey: e.shiftKey,
            altKey: e.altKey,
            metaKey: e.metaKey,
            bubbles: true,
            cancelable: true,
          }),
        );
      },
      { passive: false },
    );

    this.camera = new THREE.PerspectiveCamera(FOV, 1, 1e-6, 1e7);
    this.starCamera = new THREE.PerspectiveCamera(FOV, 1, 0.1, 1000);
    this.camera.position.set(0, -1, 0.75).normalize().multiplyScalar(1500);

    this.world = new World(data.bodies, this.profile);
    for (const [id, table] of data.tables ?? []) this.world.setBodyTable(id, table);
    const agencyColor = new Map(data.missions.agencies.map((a) => [a.id, a.color]));
    for (const mission of data.missions.missions) {
      for (const sc of mission.spacecraft) {
        // colour = agency (D17): the spacecraft's own agency (Huygens → ESA), else the mission's first
        const color = agencyColor.get(sc.agency ?? mission.agencies[0] ?? '') ?? '#ffffff';
        this.world.addSpacecraft(mission, sc.id, color);
      }
    }
    // the sky, the camera rig, bodies, sprites, trails, labels, subscriptions and input follow in populate()
  }

  // ── setup ────────────────────────────────────────────────────────────────

  /**
   * The rest of the set-up, in steps with a yield to the main thread between them (S4-03, D14 step 2: no task before
   * the first frame longer than 200 ms at 4× CPU — the constructor alone took 450 ms): the sky and the camera rig, the
   * bodies, the sprites, the start trajectories one by one, the labels, then the store subscriptions, input and the
   * first resize. Call once, before precompile(); nothing else may be called before it.
   */
  async populate(): Promise<void> {
    await yieldToMain();
    this.stars = createStarfield();
    this.milkyWay = createMilkyWay();
    this.stars.add(this.milkyWay);
    this.postfx = new PostFx(this.renderer, this.scene, this.camera, this.stars, this.starCamera);
    this.rig = new CameraRig(this.camera, this.options.canvas);
    await yieldToMain();
    this.bodies = new Bodies(this.world);
    this.scene.add(this.bodies.root);
    await yieldToMain();
    this.sprites = new SpacecraftSprites(this.world);
    this.scene.add(this.sprites.root);
    // a hidden ribbon, so the trail shader is precompiled before any trajectory has arrived (S3-02/S3-03)
    const proto = new THREE.Mesh(createRibbonGeometry(), createRibbonMaterial(createRibbonUniforms(new THREE.Color())));
    proto.visible = false;
    this.scene.add(proto);
    for (const [id, traj] of this.options.data.trajectories) {
      await yieldToMain();
      this.addTrajectory(id, traj, this.options.data.lineTimes?.get(id));
    }
    await yieldToMain();
    this.createLabels();
    await yieldToMain();
    // the stores' callbacks reach bodies, sprites and lines at once: subscribed only now that they exist
    this.subscribe();
    this.attachPointer(this.options.canvas);
    window.addEventListener('resize', this.resize);
    document.addEventListener('visibilitychange', this.onVisibility);
    this.resize();
  }

  /**
   * Installs a spacecraft's trajectory once it has loaded (S3-03): the spacecraft appears, its trail starts to
   * sample. Safe to call at any time; a second call for the same spacecraft is ignored.
   */
  addTrajectory(id: string, data: TrajectoryData, lineTimes?: Float64Array): void {
    const sc = this.world.spacecraftById.get(id);
    if (!sc || sc.trajectory) return;
    this.world.setTrajectory(id, new Trajectory(data));
    this.worldDirty = true;
    const mission = this.options.data.missions.missions.find((m) => m.id === sc.missionId);
    const markers = (mission?.events ?? [])
      .filter((e) => e.type === 'flyby' && (e.spacecraft ?? sc.id) === sc.id)
      .map((e) => ({ t: utcMsToTdbDays(parseUtc(e.date)), type: e.type }));
    const line = new TrajectoryLine(this.world, sc, markers, this.diamond, lineTimes);
    const silent = silentFrom(sc.life);
    const phase = mission?.spacecraft.find((x) => x.id === id)?.trajectory.phase ?? null;
    line.setStyle({ silentFrom: silent === null ? Infinity : utcMsToTdbDays(silent), phase });
    line.setResolution(this.width, this.height);
    line.setHdr(this.postfx?.quality !== undefined && this.postfx.quality !== 'low');
    line.setHighlight(this.highlightOf(sc.missionId));
    this.lines.push(line);
    this.lineById.set(id, line);
    this.scene.add(line.root);
  }

  /** Installs a HORIZONS body table once it has loaded (S3-03/S3-06). */
  addBodyTable(id: string, table: BodyTable): void {
    this.world.setBodyTable(id, table);
    this.worldDirty = true;
  }

  /** Bands for the subdivision of trajectory lines, by centre body (handed to the loader's worker). */
  lineBands(): Record<string, MoonBand[]> {
    const out: Record<string, MoonBand[]> = { sun: [...this.world.moonBands('sun')] };
    for (const b of this.world.planets) {
      const bands = this.world.moonBands(b.data.id);
      if (bands.length) out[b.data.id] = [...bands];
    }
    return out;
  }

  /** The lazy loader (debugState shows its queue). */
  loader: { queue(): string[] } | null = null;

  private readonly lineById = new Map<string, TrajectoryLine>();
  /** The real "now" in mission time: trails are dashed after it (predicted, Concept §5). */
  private readonly predictedFrom = utcMsToTdbDays(Date.now());
  private readonly diamond = diamondTexture();
  /** The selected mission (D9: highlighted, the others dimmed), or null. */
  private selectedMission: string | null = null;

  private highlightOf(missionId: string): Highlight {
    if (!this.selectedMission) return 'normal';
    return missionId === this.selectedMission ? 'selected' : 'dimmed';
  }

  /** Highlights the selected mission's trail and beacons (D9); null = all equal. */
  setSelectedMission(missionId: string | null): void {
    if (missionId === this.selectedMission) return;
    this.selectedMission = missionId;
    for (const line of this.lines) {
      const sc = this.world.spacecraft.find((x) => this.lineById.get(x.id) === line);
      if (sc) line.setHighlight(this.highlightOf(sc.missionId));
    }
  }

  private createLabels(): void {
    const onActivate = (id: string): void => focusOn(id);
    for (const v of this.bodies.visuals) {
      const b = v.body.data;
      const label = createLabel(b.id, b.name.en, b.color, onActivate);
      label.basePriority = b.id === 'sun' ? 50 : Math.round(Math.log10(b.radius) * 10);
      label.protectAnchor = !b.parent || b.parent === 'sun';
      v.group.add(label.object);
      this.labels.push(label);
    }
    for (const v of this.sprites.visuals) {
      const label = createLabel(v.sc.id, v.sc.name, v.sc.color, onActivate, 'spacecraft');
      label.basePriority = 80;
      v.sprite.add(label.object);
      this.labels.push(label);
      // a named landing site ("Tranquility Base", S4-11) on the site marker; a tap focuses the lander
      const site = this.options.data.missions.missions
        .find((m) => m.id === v.sc.missionId)
        ?.spacecraft.find((x) => x.id === v.sc.id)?.landing?.site;
      if (!site) continue;
      const id = `${v.sc.id}${SITE_SUFFIX}`;
      const siteLabel = createLabel(id, site.en, v.sc.color, () => focusOn(v.sc.id), 'spacecraft');
      siteLabel.basePriority = 70;
      v.endMarker.add(siteLabel.object);
      this.labels.push(siteLabel);
      this.siteNames.set(id, site);
    }
    // in the layer from the start, hidden until placed (the renderer would add each on its first visible frame, and
    // a label decluttered from the first frame on would never be in the document)
    for (const label of this.labels) {
      label.object.element.style.display = 'none';
      this.options.labelLayer.append(label.object.element);
    }
  }

  private readonly tmpAnchorP = vec3();
  private readonly sampleBudgetMs: number | undefined;
  /** Ring of per-frame CPU times (perf scenarios). */
  private readonly cpuSamples = new Float32Array(1024);
  private cpuCount = 0;
  /** Date, profile and data the world was last computed for (see frame). */
  private worldTdb = Number.NaN;
  private worldProfile = -1;
  private worldDirty = true;

  /** Labels of named landing sites (S4-11): label id → name. */
  private readonly siteNames = new Map<string, LocalizedText>();

  private updateLabelTexts(l: Lang): void {
    this.labelLang = l;
    for (const label of this.labels) {
      const body = this.world.bodyById.get(label.id);
      const site = this.siteNames.get(label.id);
      if (body) setLabelText(label, body.data.name[l]);
      else if (site) setLabelText(label, site[l]);
      else this.spacecraftLabelText(label);
    }
  }

  private labelLang: Lang = 'en';

  /** A spacecraft's label: its name, with the suffix "silent" after loss of contact (D11). */
  private spacecraftLabelText(label: Label): void {
    const sc = this.world.spacecraftById.get(label.id);
    const name = sc?.name ?? label.id;
    setLabelText(label, sc?.lifeState === 'silent' ? `${name} · ${translate(this.labelLang, 'life.silent')}` : name);
  }

  /** A body's true radius, km (title cards: altitude above the surface). */
  bodyRadiusKm(id: string): number {
    return this.world.bodyById.get(id)?.mapped.radius ?? 0;
  }

  /** Life cycle of a spacecraft now (telemetry, panel): state and, if silent, since when (UTC ms). */
  lifeOf(id: string): { state: string; silentSince: number | null } | null {
    const sc = this.world.spacecraftById.get(id);
    if (!sc) return null;
    return { state: sc.lifeState, silentSince: sc.lifeState === 'silent' ? sc.life.end : null };
  }

  /** First moment a spacecraft is shown (its trajectory's start, else its launch), UTC ms. */
  flightStart(id: string): number | null {
    const sc = this.world.spacecraftById.get(id);
    return sc ? (sc.life.coverage?.[0] ?? sc.life.launch) : null;
  }

  /** Life-cycle state of a spacecraft at another date (UTC ms), e.g. where a chronology entry jumps to. */
  lifeStateAt(id: string, ms: number): string | null {
    const sc = this.world.spacecraftById.get(id);
    return sc ? lifeState(sc.life, ms) : null;
  }

  private subscribe(): void {
    this.unsubscribers.push(
      qualityPref.subscribe((pref) => {
        if (pref === 'auto') {
          this.qualityAuto = true;
          this.applyQuality(this.qualityMonitor.tier);
        } else {
          this.qualityAuto = false;
          this.applyQuality(pref);
        }
      }),
      currentDate.subscribe((v) => (this.dateMs = v)),
      flybyTimes.subscribe((times) => (this.flybys = times)),
      motionReduced.subscribe((v) => (this.motionReduced = v)),
      cinematicCamera.subscribe((v) => (this.cinematicOn = v)),
      scaleMode.subscribe((m: ScaleMode) => {
        this.mixTarget = m === 'realistic' ? 1 : 0;
        // the initial mode (e.g. from a deep link) applies without animation
        if (this.motionReduced || !this.started) this.mix = this.mixTarget;
      }),
      focus.subscribe((id) => {
        if (id !== this.focusId || this.pendingFlight) {
          this.focusId = id;
          this.pendingFlight = true;
        }
      }),
      // the same object again (chronology after a date jump): fly to where it is now, framed anew
      refocus.subscribe((n) => {
        if (n > 0) this.pendingFlight = true;
      }),
      followTarget.subscribe((id) => {
        const was = this.followId;
        this.followId = id;
        // leaving follow mode: keep the view where it is (anchor at the current spacecraft position)
        if (was && !id) this.anchorAtSpacecraft(was);
      }),
      lang.subscribe((l) => this.updateLabelTexts(l)),
      selectedMission.subscribe((m) => this.setSelectedMission(m)),
    );
  }

  private attachPointer(canvas: HTMLCanvasElement): void {
    canvas.addEventListener('pointerdown', (e) => {
      this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY, time: e.timeStamp, moved: false });
      if (this.pointers.size === 1) this.gesture = { maxPointers: 1, start: e.timeStamp };
      else if (this.gesture) this.gesture.maxPointers = Math.max(this.gesture.maxPointers, this.pointers.size);
    });
    canvas.addEventListener('pointermove', (e) => {
      const p = this.pointers.get(e.pointerId);
      if (p && Math.hypot(e.clientX - p.x, e.clientY - p.y) > TAP_SLOP_PX) p.moved = true;
    });
    const release = (e: PointerEvent): { x: number; y: number; time: number; moved: boolean } | undefined => {
      const p = this.pointers.get(e.pointerId);
      this.pointers.delete(e.pointerId);
      if (p && this.gesture && p.moved) this.gesture.moved = true;
      return p;
    };
    canvas.addEventListener('pointercancel', (e) => {
      release(e);
      if (this.pointers.size === 0) this.gesture = null;
    });
    canvas.addEventListener('pointerup', (e) => {
      const down = release(e);
      const gesture = this.gesture;
      if (this.pointers.size > 0) return; // wait for the last finger
      this.gesture = null;
      if (!down || !gesture || gesture.moved || down.moved) return;
      // event timestamps (input time), so a busy main thread does not break taps or double taps
      if (e.timeStamp - gesture.start > TAP_MAX_MS) return;
      if (gesture.maxPointers === 2) {
        this.rig.zoomBy(ZOOM_OUT_STEP, null, this.motionReduced); // two-finger tap: zoom out (S2-06)
        return;
      }
      if (gesture.maxPointers > 2) return;
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const id = pick(this.pickables(), this.camera, x, y, this.width, this.height);
      const now = e.timeStamp;
      if (!id) {
        // double tap on empty space: zoom in one step towards that point (S2-06)
        const last = this.lastEmptyTap;
        if (now - last.time < DOUBLE_TAP_MS && Math.hypot(x - last.x, y - last.y) < DOUBLE_TAP_SLOP_PX) {
          this.rig.zoomBy(ZOOM_IN_STEP, this.pointAtFocusDepth(x, y), this.motionReduced);
          this.lastEmptyTap = { x: 0, y: 0, time: -Infinity };
        } else {
          this.lastEmptyTap = { x, y, time: now };
        }
        return;
      }
      this.lastEmptyTap = { x: 0, y: 0, time: -Infinity };
      const isSpacecraft = this.world.spacecraftById.has(id);
      if (isSpacecraft && this.lastTap.id === id && now - this.lastTap.time < DOUBLE_TAP_MS) {
        follow(id);
      } else {
        focusOn(id);
      }
      this.lastTap = { id, time: now };
    });
  }

  /**
   * Display-space offset from the origin (du) of the point under screen position (x, y) on the plane through the
   * origin facing the camera — the zoom target of a double tap.
   */
  private pointAtFocusDepth(x: number, y: number): Vec3 {
    const cam = this.camera.position;
    const dir = new THREE.Vector3((x / this.width) * 2 - 1, 1 - (y / this.height) * 2, 0.5)
      .unproject(this.camera)
      .sub(cam)
      .normalize();
    const n = cam.clone().negate().normalize();
    const denom = dir.dot(n);
    if (Math.abs(denom) < 1e-9) return vec3();
    const t = -cam.dot(n) / denom;
    return vec3(cam.x + dir.x * t, cam.y + dir.y * t, cam.z + dir.z * t);
  }

  /**
   * Pickable objects. Progressive disclosure: objects inside a bubble (spacecraft, moons) can only be picked once
   * that bubble is at least MIN_BUBBLE_PICK_PX on screen; before that a tap picks the planet itself (S1-12: "Jupiter
   * with one tap from the default view, Voyager 2 with one tap inside the Jupiter bubble").
   */
  private pickables(): Pickable[] {
    const view = this.viewInfo();
    const bubbleOpen = (bodyId: string | null | undefined): boolean => this.bubbleOpen(bodyId, view);
    const list: Pickable[] = [];
    for (const v of this.bodies.visuals) {
      if (!v.body.active || !bubbleOpen(v.body.parent?.data.id) || v.blur >= 0.5) continue;
      list.push({
        id: v.body.data.id,
        position: v.group.position.clone(),
        // the disc, or the minimum-size marker drawn in its place
        radiusPx: Math.max(this.bodies.projectedRadiusPx(v.body.data.id, view), this.profile.spriteMinPx / 2),
        priority: v.body.parent ? 1 : 0,
      });
    }
    for (const v of this.sprites.visuals) {
      const bubble = v.sc.bubble ? this.world.bodyById.get(v.sc.bubble) : undefined;
      // inside a moon's nested bubble the planet's bubble decides (the moon's is small on screen)
      if (!v.sprite.visible || !bubbleOpen(bubble?.parent?.data.id ?? v.sc.bubble)) continue;
      const inside = bubble ? [bubble.data.id, ...(bubble.parent ? [bubble.parent.data.id] : [])] : [];
      list.push({ id: v.sc.id, position: v.sprite.position.clone(), radiusPx: 0, priority: 2, inside });
    }
    return list;
  }

  // ── lifecycle ────────────────────────────────────────────────────────────

  /**
   * Compiles every shader the first frames need, up front (S3-02): the scene and the sky in their canvas variant. The
   * first frames render plainly, as on the 'low' tier (S4-03, D14): the post-processing chain and the HDR variants
   * (the program variants differ for the half-float target) follow after the first frame in idle time
   * (prepareHdr), and the scene then switches to its tier with the bloom fading in. In parallel where
   * KHR_parallel_shader_compile is available.
   */
  async precompile(): Promise<void> {
    // the first frame need not repeat it (S4-03 part 6)
    this.worldTdb = utcMsToTdbDays(this.dateMs);
    this.worldProfile = this.profileVersion;
    this.worldDirty = false;
    this.world.update(this.worldTdb);
    this.warm = true;
    this.applyQuality(this.requestedTier);
    // one top-level object per task (S4-03): compiling everything at once was a 300–700 ms task at 4× CPU
    for (const [root, camera] of [
      [this.scene, this.camera],
      [this.stars, this.starCamera],
    ] as const) {
      for (const child of [...root.children]) {
        await yieldToMain();
        await this.compileFor(child, camera, root as THREE.Scene, false);
      }
    }
    await this.warmDraw();
  }

  /**
   * Draws every drawable once, a few per task, into one pixel of the canvas that is cleared again: geometry buffers,
   * vertex arrays and uniforms go to the GPU here instead of in the first frame (S4-03 part 6: the first frame was a
   * 120–130 ms task at 4× CPU after the precompile). The same lights are on, so the programs are the compiled ones.
   */
  private async warmDraw(): Promise<void> {
    const r = this.renderer;
    const viewport = r.getViewport(new THREE.Vector4());
    const scissor = r.getScissor(new THREE.Vector4());
    const scissorTest = r.getScissorTest();
    for (const [root, camera] of [
      [this.scene, this.camera],
      [this.stars, this.starCamera],
    ] as const) {
      const drawables: THREE.Object3D[] = [];
      root.traverseVisible((o) => {
        if ((o as Partial<THREE.Mesh>).material) drawables.push(o);
      });
      const culled = drawables.map((o) => o.frustumCulled);
      for (const o of drawables) o.visible = false;
      let i = 0;
      try {
        while (i < drawables.length) {
          await yieldToMain();
          const sliceEnd = performance.now() + COMPILE_SLICE_MS;
          while (i < drawables.length && performance.now() < sliceEnd) {
            const batch = drawables.slice(i, i + WARM_DRAW_BATCH);
            i += batch.length;
            for (const o of batch) {
              o.visible = true;
              o.frustumCulled = false;
            }
            r.setViewport(0, 0, 1, 1);
            r.setScissor(0, 0, 1, 1);
            r.setScissorTest(true);
            r.render(root, camera);
            r.clear();
            for (const o of batch) o.visible = false;
          }
        }
      } finally {
        drawables.forEach((o, k) => {
          o.visible = true;
          o.frustumCulled = culled[k]!;
        });
        r.setViewport(viewport);
        r.setScissor(scissor);
        r.setScissorTest(scissorTest);
      }
    }
  }

  /**
   * Loads the post-processing chain and compiles the HDR variants of the scene, the sky and the chain — one top-level
   * object per task, so no task grows long (S4-03: TTI needs five quiet seconds) — then applies the
   * requested tier. Once; later calls return the same promise.
   */
  private prepareHdr(): Promise<void> {
    return (this.hdrPrepared ??= (async () => {
      await this.postfx.load();
      for (const [root, camera] of [
        [this.scene, this.camera],
        [this.stars, this.starCamera],
      ] as const) {
        for (const child of [...root.children]) {
          // a task boundary, not idle time: while frames render continuously there is none (three-second waits)
          await yieldToMain();
          await this.compileFor(child, camera, root as THREE.Scene, true);
        }
      }
      await this.postfx.warmUp(this.requestedTier === 'low' ? 'medium' : this.requestedTier, yieldToMain, this.asHdr);
      this.hdrReady = true;
      this.applyQuality(this.requestedTier);
    })());
  }

  /** Runs `fn` with the objects that only post-processed tiers draw made visible (for compiling and warm-up). */
  private readonly asHdr = (fn: () => void): void => {
    const hidden = !this.milkyWay.visible;
    this.milkyWay.visible = true;
    try {
      fn();
    } finally {
      if (hidden) this.milkyWay.visible = false;
    }
  };

  private hdrPrepared: Promise<void> | null = null;
  /** The HDR variants are compiled and the chain is loaded: a post-processed tier may render. */
  private hdrReady = false;

  /**
   * Compiles `object` (inside `target`, for its lights) for the post-processing target (`hdr`) or the canvas, one
   * drawable after another and at most COMPILE_SLICE_MS per task (S4-03 part 6: one top-level object, e.g. all the
   * bodies, was a 120–180 ms task at 4× CPU, and Lighthouse counts every task above 50 ms).
   */
  private async compileFor(
    object: THREE.Object3D,
    camera: THREE.Camera,
    target: THREE.Scene | undefined,
    hdr: boolean,
  ): Promise<void> {
    const r = this.renderer;
    // compileAsync warns without KHR_parallel_shader_compile (e.g. SwiftShader); compile synchronously there
    const parallel = r.extensions.has('KHR_parallel_shader_compile');
    const renderTarget = hdr ? await this.postfx.sceneTarget() : null;
    const drawables: THREE.Object3D[] = [];
    object.traverse((o) => {
      if ((o as Partial<THREE.Mesh>).material) drawables.push(o);
    });
    const pending: Promise<unknown>[] = [];
    let sliceEnd = performance.now() + COMPILE_SLICE_MS;
    const slice = async (): Promise<void> => {
      if (performance.now() < sliceEnd) return;
      await yieldToMain();
      sliceEnd = performance.now() + COMPILE_SLICE_MS;
    };
    for (const drawable of drawables) {
      await slice();
      const previous = r.getRenderTarget();
      r.setRenderTarget(renderTarget);
      const compile = (): void => {
        if (parallel) pending.push(r.compileAsync(drawable, camera, target ?? null));
        else r.compile(drawable, camera, target ?? null);
      };
      // three.js compiles visible objects only: the Milky Way is hidden until a post-processed tier shows it
      if (hdr) this.asHdr(compile);
      else compile();
      r.setRenderTarget(previous);
    }
    await Promise.all(pending);
    // three.js reads a program's info logs, uniforms and attributes on its first draw: synchronous round trips to the
    // GPU process that otherwise land in the first frame and the first post-processed one (S4-03); without parallel
    // compile, the first read also waits for the link
    for (const program of r.info.programs ?? []) {
      await slice();
      program.getUniforms();
      program.getAttributes();
    }
    // the rest of a drawable's first draw (S4-03 part 6: the first frame was a 150 ms task at 4× CPU): the bounding
    // sphere for culling and, for the canvas pass, the textures it already has (loaded maps go up one per frame). Only
    // where the first frame would compute it: unculled geometries change their vertices later, and picking computes
    // their sphere on first use
    for (const drawable of drawables) {
      await slice();
      const geometry = (drawable as Partial<THREE.Mesh>).geometry;
      if (geometry && drawable.frustumCulled && !geometry.boundingSphere) geometry.computeBoundingSphere();
      if (!hdr) for (const texture of texturesOf(drawable)) r.initTexture(texture);
    }
  }

  start(): void {
    this.lastTime = performance.now();
    this.renderer.setAnimationLoop(this.frame);
    if (!this.texturesRequested) {
      this.texturesRequested = true;
      // after the first frames, so textures never delay the first render (S1-20)
      const phone = Math.min(window.screen.width, window.screen.height) < 600;
      this.assetsReady = new Promise<void>((resolve) =>
        setTimeout(() => {
          void Promise.all([
            this.bodies.loadTextures(phone ? '1k' : '2k'),
            this.loadStarCatalog(),
            this.loadOrbits(),
          ]).then(() => resolve());
        }, 200),
      );
    }
  }

  /** Trail sampling budget of the next task between frames (0: none), set by each frame. */
  private betweenFramesBudgetMs = 0;
  private betweenFramesQueued = false;

  /** The bulk of the trail sampling, in a task of its own after a frame (S4-03 part 6, TrajectoryLine). */
  private readonly sampleBetweenFrames = (): void => {
    this.betweenFramesQueued = false;
    if (this.betweenFramesBudgetMs <= 0) return;
    beginBetweenFrames(this.betweenFramesBudgetMs);
    for (const line of this.lines) line.sampleBetweenFrames();
  };

  /** Resolves when textures and the star catalogue are on the GPU (the intro sweep waits for it). */
  private assetsReady: Promise<void> = Promise.resolve();

  stop(): void {
    this.renderer.setAnimationLoop(null);
  }

  /** The planets' orbit lines (orbits.json, after the first frame like the textures: S4-03 part 6). */
  private async loadOrbits(): Promise<void> {
    try {
      const res = await fetch(`${import.meta.env.BASE_URL}data/orbits.json`);
      this.bodies.setOrbits(res.ok ? ((await res.json()) as OrbitsFile) : null);
    } catch {
      this.bodies.setOrbits(null);
    }
  }

  /** Replaces the procedural starfield with the real sky once stars.json has loaded (S1-24). */
  private async loadStarCatalog(): Promise<void> {
    try {
      const res = await fetch(`${import.meta.env.BASE_URL}data/stars.json`);
      if (!res.ok) return;
      const points = createCatalogStars((await res.json()) as StarsFile, this.dpr);
      // compiled before it is shown, so the swap does not stall a frame (S3-02)
      await this.compileFor(points, this.starCamera, this.stars, this.postfx.quality !== 'low');
      // swap only the procedural points; the Milky Way stays
      for (const old of this.stars.children.filter((o) => o instanceof THREE.Points)) this.stars.remove(old);
      this.stars.add(points);
    } catch {
      // keep the procedural stars
    }
  }

  private readonly onVisibility = (): void => {
    if (document.hidden) this.stop();
    else {
      this.qualityMonitor.reset();
      this.start();
    }
  };

  private updateCinematics(dt: number): void {
    const sc = this.followId ? this.world.spacecraftById.get(this.followId) : undefined;
    const flyby = nearestFlyby(this.dateMs, this.flybys);
    const inWindow = !!sc?.visible && !!sc.bubble && Math.abs(this.dateMs - flyby) < CINE_WINDOW_MS;
    const c = this.cine;
    if (!inWindow || this.motionReduced || !this.cinematicOn) {
      if (c && !c.done && this.rig.inputCount === c.inputMark) {
        // ease back to the distance from before the flyby, then end
        this.rig.easeDistance(c.restoreDist, dt, CINE_TAU_S);
        if (Math.abs(Math.log(this.rig.distance / c.restoreDist)) < 0.01) this.cine = null;
      } else this.cine = null;
      return;
    }
    if (!c || c.flyby !== flyby) {
      this.cine = { flyby, restoreDist: this.rig.distance, inputMark: this.rig.inputCount, done: false };
      return;
    }
    if (c.done || this.rig.inputCount !== c.inputMark) {
      c.done = true; // the user took over: no automatic moves until the next flyby
      return;
    }
    const body = this.world.bodyById.get(sc!.bubble!);
    if (!body) return;
    const sep = Math.hypot(sc!.d.x - body.d.x, sc!.d.y - body.d.y, sc!.d.z - body.d.z);
    const target = Math.max(this.fit(CINE_MARGIN * (sep + body.r)), c.restoreDist * 0.5);
    this.rig.easeDistance(target, dt, CINE_TAU_S);
  }

  /**
   * Intro sequence (S2-12): a dark hold until the scene is warm (see INTRO_HOLD_MIN_S), then the Sun ignites over 1.2 s
   * while the camera sweeps in from far out and half a turn around (INTRO_SWEEP_S). With reduced motion only the
   * ignition plays (no camera move). Ends early with `skipIntro`. Resolves when the hold ends (the title and the end
   * of the intro are timed from there).
   */
  playIntro(reducedMotion: boolean): Promise<void> {
    this.warmUp();
    this.bodies.setIgnition(0);
    // started in the frame loop, after the initial framing flight (which would otherwise replace the sweep)
    this.introSweepPending = !reducedMotion;
    return new Promise((resolve) => {
      this.introHold = { held: 0, ready: false, started: resolve, bench: { time: 0, samples: [] } };
      void this.assetsReady
        .then(() => (this.requestedTier !== 'low' ? this.prepareHdr() : undefined))
        .then(() => {
          if (this.introHold) this.introHold.ready = true;
        });
    });
  }

  /** The dark hold of the intro: ends once everything is warm (or after INTRO_HOLD_MAX_S). */
  private updateIntroHold(dt: number): void {
    const hold = this.introHold;
    if (!hold) return;
    hold.held += dt;
    const warm = hold.ready && this.lines.every((l) => l.ready);
    if (hold.held < INTRO_HOLD_MIN_S || (!warm && hold.held < INTRO_HOLD_MAX_S)) return;
    if (warm && this.qualityAuto && hold.held < INTRO_HOLD_MAX_S && this.benchIntro(hold.bench, dt)) return;
    this.introHold = null;
    this.introTime = 0;
    this.rig.releaseHold();
    hold.started();
  }

  /** Results of the intro's frame-time measurement (debugState). */
  private readonly introBench: { tier: Quality; medianMs: number }[] = [];

  /** Frame-time measurement in the dark hold (INTRO_BENCH_S). Returns true while the hold should go on. */
  private benchIntro(bench: { time: number; samples: number[] }, dt: number): boolean {
    bench.time += dt;
    bench.samples.push(dt * 1000);
    if (bench.time < INTRO_BENCH_S) return true;
    const sorted = bench.samples.sort((a, b) => a - b);
    const median = sorted[sorted.length >> 1]!;
    bench.time = 0;
    bench.samples.length = 0;
    const tier = this.qualityMonitor.tier;
    this.introBench.push({ tier, medianMs: Math.round(median * 10) / 10 });
    if (median <= INTRO_BENCH_BUDGET_MS || tier === 'low') return false;
    const next = QUALITY_TIERS[QUALITY_TIERS.indexOf(tier) + 1]!;
    this.qualityMonitor.tier = next;
    this.applyQuality(next);
    return true;
  }

  skipIntro(): void {
    this.introHold?.started();
    this.introHold = null;
    this.introSweepPending = false;
    this.introTime = Number.NaN;
    this.bodies.setIgnition(1);
    this.rig.finishFlight();
  }

  /** Ends the start-up phase: the requested rendering tier applies from now on. */
  private warmUp(): void {
    clearTimeout(this.warmupTimer);
    if (this.warm) return;
    this.warm = true;
    this.qualityMonitor.reset();
    this.applyQuality(this.requestedTier);
  }

  /**
   * Switches the rendering tier (post-processing and HDR gains). Before precompile() the scene would render at 'low';
   * since Sprint 3 the start tier's shaders are compiled asynchronously before the first frame, so the scene starts
   * in the requested tier right away.
   */
  private applyQuality(tier: Quality): void {
    const dprChanged = TIER_MAX_DPR[tier] !== TIER_MAX_DPR[this.requestedTier];
    this.requestedTier = tier;
    // fewer pixels on a lower tier (S4-15)
    if (dprChanged && this.width > 0) this.resize();
    if (!this.warm) tier = 'low';
    // a post-processed tier waits for the chain and its shaders (S4-03); prepareHdr applies it when they are ready
    if (tier !== 'low' && !this.hdrReady) {
      if (this.started) void this.prepareHdr();
      tier = 'low';
    }
    this.postfx.setQuality(tier);
    const hdr = tier !== 'low';
    this.bodies.setHdr(hdr);
    // the procedural Milky Way is a full-screen noise shader: skipped on the lowest tier (weak GPUs)
    this.milkyWay.visible = hdr;
    // the high tier shows a richer sky
    ((this.milkyWay.material as THREE.ShaderMaterial).uniforms['uBrightness'] as THREE.IUniform<number>).value =
      tier === 'high' ? 1.6 : 1;
    this.sprites.setHdr(hdr);
    for (const line of this.lines) line.setHdr(hdr);
  }

  private readonly resize = (): void => {
    const canvas = this.options.canvas;
    this.width = canvas.clientWidth || window.innerWidth;
    this.height = canvas.clientHeight || window.innerHeight;
    this.dpr = Math.min(window.devicePixelRatio || 1, this.maxDpr, TIER_MAX_DPR[this.requestedTier]);
    this.renderer.setPixelRatio(this.dpr);
    this.renderer.setSize(this.width, this.height, false);
    this.postfx?.setSize(this.width, this.height);
    for (const line of this.lines) line.setResolution(this.width, this.height);
    this.labelRenderer.setSize(this.width, this.height);
    for (const cam of [this.camera, this.starCamera]) {
      cam.aspect = this.width / this.height;
      cam.updateProjectionMatrix();
    }
    this.applyViewOffset();
    this.stars.traverse((o) => {
      if (o instanceof THREE.Points && o.material instanceof THREE.ShaderMaterial) {
        (o.material.uniforms['uDpr'] as THREE.IUniform<number>).value = this.dpr;
      } else if (o instanceof THREE.Points) {
        const mat = o.material as THREE.PointsMaterial;
        mat.userData['base'] ??= mat.size;
        mat.size = (mat.userData['base'] as number) * this.dpr;
      }
    });
  };

  dispose(): void {
    this.stop();
    clearTimeout(this.warmupTimer);
    for (const u of this.unsubscribers) u();
    window.removeEventListener('resize', this.resize);
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.rig.dispose();
    this.postfx.dispose();
    this.renderer.dispose();
  }

  // ── per-frame helpers ────────────────────────────────────────────────────

  private viewInfo(): ViewInfo {
    return {
      camera: this.camera,
      pxPerDuAtUnit: this.height / (2 * Math.tan(THREE.MathUtils.degToRad(this.camera.fov) / 2)),
    };
  }

  /** Interpolates the profile; returns true if any parameter changed this frame. */
  private updateProfile(dt: number): boolean {
    if (this.mix !== this.mixTarget) {
      const step = dt / MODE_SWITCH_SECONDS;
      this.mix =
        this.mixTarget > this.mix
          ? Math.min(this.mixTarget, this.mix + step)
          : Math.max(this.mixTarget, this.mix - step);
    }
    interpolateProfile(this.settings.overview, this.settings.realistic, easeInOut(this.mix), this.profile);
    const key = JSON.stringify(this.profile);
    if (key === this.profileKey) return false;
    this.profileKey = key;
    this.profileVersion++;
    return true;
  }

  /**
   * Camera distance at which a sphere of `radius` (du) around the focus fits the narrower axis of the free view
   * (the canvas minus the HUD insets and the detail panel).
   */
  private fit(radius: number): number {
    const tanHalf = Math.tan(THREE.MathUtils.degToRad(FOV) / 2);
    const freeHeight = this.height - this.insetsTarget.top - this.insetsTarget.bottom;
    const freeWidth = this.width - this.insetsTarget.right;
    return radius / ((tanHalf * Math.min(freeWidth, freeHeight)) / this.height);
  }

  /**
   * Tells the scene how much of the canvas the HUD covers at the top and bottom and the detail panel on the right
   * (px). The projection centre moves to the middle of the free area (camera view offset, eased) and framing fits
   * into it (S2-06, S3-16).
   */
  setViewInsets(top: number, bottom: number, right = 0): void {
    // keep at least 40 % of the canvas as free view, whatever the HUD reports
    const maxTotal = 0.6 * this.height;
    const scale = top + bottom > maxTotal ? maxTotal / (top + bottom) : 1;
    this.insetsTarget.top = Math.max(0, top * scale);
    this.insetsTarget.bottom = Math.max(0, bottom * scale);
    this.insetsTarget.right = Math.min(Math.max(0, right), 0.6 * this.width);
    if (!this.started) {
      Object.assign(this.insets, this.insetsTarget);
      this.applyViewOffset();
    }
  }

  private applyViewOffset(): void {
    const shiftY = (this.insets.top - this.insets.bottom) / 2;
    const shiftX = this.insets.right / 2;
    for (const cam of [this.camera, this.starCamera]) {
      if (Math.abs(shiftY) < 0.5 && Math.abs(shiftX) < 0.5) cam.clearViewOffset();
      else cam.setViewOffset(this.width, this.height, shiftX, -shiftY, this.width, this.height);
    }
  }

  /** Eases the view offset towards the reported insets (no jump when e.g. the follow pill or the panel appears). */
  private updateViewOffset(dt: number): void {
    const t = this.insetsTarget;
    const c = this.insets;
    const near = (k: 'top' | 'bottom' | 'right'): boolean => Math.abs(t[k] - c[k]) < 0.5;
    if (near('top') && near('bottom') && near('right')) {
      if (t.top === c.top && t.bottom === c.bottom && t.right === c.right) return;
      Object.assign(c, t);
    } else {
      const k = this.motionReduced ? 1 : smoothingFactor(dt, 0.2);
      c.top += (t.top - c.top) * k;
      c.bottom += (t.bottom - c.bottom) * k;
      c.right += (t.right - c.right) * k;
    }
    this.applyViewOffset();
  }

  /** Default camera distance when focusing an object (fits the narrower axis, so phones in portrait work too). */
  private framingDistance(id: string): number {
    // whole system: Neptune's display orbit, widened to the spacecraft on screen (S4-25: today the Voyagers are at
    // 140–170 AU, twice Neptune's display distance); 20 % room for their labels
    if (id === 'sun') {
      let radius = 1.15 * this.world.mapping.g(30.1 * AU_KM);
      for (const sc of this.world.spacecraft) if (sc.visible) radius = Math.max(radius, 1.2 * length(sc.d));
      return this.fit(radius);
    }
    const body = this.world.bodyById.get(id);
    // moons: some of the planet's neighbourhood
    if (body?.parent) return this.fit(7 * body.r);
    // planets: detail zoom into the bubble, widened so that spacecraft currently inside it and its moons stay in view
    // (S4-06: the Moon lies 1.7 du from Earth, outside Earth's detail zoom on a phone)
    if (body) {
      let radius = Math.max(0.42 * this.world.displayBubbleRadius(id), 2.3 * body.r);
      for (const sc of this.world.spacecraft) {
        if (!sc.visible || sc.bubble !== id) continue;
        radius = Math.max(radius, 1.15 * Math.hypot(sc.d.x - body.d.x, sc.d.y - body.d.y, sc.d.z - body.d.z));
      }
      for (const moon of this.world.moons) {
        if (moon.parent !== body || !moon.active) continue;
        radius = Math.max(radius, 1.15 * Math.hypot(moon.d.x - body.d.x, moon.d.y - body.d.y, moon.d.z - body.d.z));
      }
      return this.fit(radius);
    }
    const sc = this.world.spacecraftById.get(id);
    // an ended spacecraft: its end marker; a lander's with the body it landed on in view (the camera used to end up
    // inside Titan — maintainer feedback)
    if (sc && !sc.visible && sc.endVisible) {
      const landed = sc.landing ? this.world.bodyById.get(sc.landing.body) : undefined;
      return landed ? this.fit(LANDED_FRAMING * landed.r) : Math.max(0.5, 0.08 * length(sc.end));
    }
    const ctx = sc?.bubble ? this.world.bodyById.get(sc.bubble) : undefined;
    if (sc && ctx) {
      // keep the planet in view
      const d = Math.hypot(sc.d.x - ctx.d.x, sc.d.y - ctx.d.y, sc.d.z - ctx.d.z);
      return this.fit(Math.max(1.4 * d, 2 * ctx.r));
    }
    return Math.max(0.5, 0.08 * (sc ? length(sc.d) : 100));
  }

  /** Reference length of the focused object: keeps its apparent size when the scale mode changes (S1-14). */
  private referenceLength(): number {
    const id = this.focusId;
    if (id === 'sun') return this.world.mapping.g(30.1 * AU_KM);
    const body = this.world.bodyById.get(id);
    if (body) return body.r;
    const sc = this.world.spacecraftById.get(id);
    if (!sc) return 1;
    const ctx = sc.bubble ? this.world.bodyById.get(sc.bubble) : undefined;
    if (ctx) return Math.hypot(sc.d.x - ctx.d.x, sc.d.y - ctx.d.y, sc.d.z - ctx.d.z);
    return length(sc.d);
  }

  /** Focus on a landed spacecraft (a point on a surface): the camera keeps out of the body it landed on. */
  private landedClearance(): number {
    const sc = this.world.spacecraftById.get(this.focusId);
    if (!sc?.landing || sc.visible || !sc.endVisible) return 0;
    const body = this.world.bodyById.get(sc.landing.body);
    return body ? 2.2 * body.r : 0;
  }

  private anchorAtSpacecraft(id: string): void {
    const sc = this.world.spacecraftById.get(id);
    if (!sc?.visible) return;
    const body = sc.bubble ? this.world.bodyById.get(sc.bubble) : undefined;
    this.anchor = body
      ? { body: body.data.id, q: vec3(sc.state.p.x - body.p.x, sc.state.p.y - body.p.y, sc.state.p.z - body.p.z) }
      : { body: null, q: vec3(sc.state.p.x, sc.state.p.y, sc.state.p.z) };
  }

  /** Display position of the current focus point (body, followed spacecraft or anchored spacecraft view). */
  private resolveFocus(out: Vec3): Vec3 {
    const id = this.focusId;
    const body = this.world.bodyById.get(id);
    if (body) return Object.assign(out, body.d);
    const sc = this.world.spacecraftById.get(id);
    if (!sc) return Object.assign(out, this.world.bodyById.get('sun')!.d);
    if (!sc.visible && sc.endVisible) return Object.assign(out, sc.end);
    if (this.followId === id || !this.anchor) {
      if (sc.visible) Object.assign(out, sc.d);
      return out;
    }
    const m = this.world.mapping;
    const anchorBody = this.anchor.body ? this.world.bodyById.get(this.anchor.body) : undefined;
    if (anchorBody?.parent) {
      // inside a moon's nested bubble (Eagle on the Moon, S4-11): mapped like the spacecraft itself (World.update),
      // with the full chain — mapRelative knows only the planet's bubble and put Eagle 3,500 px off the screen
      const q = this.anchor.q;
      const p = this.tmpAnchorP;
      p.x = anchorBody.p.x + q.x;
      p.y = anchorBody.p.y + q.y;
      p.z = anchorBody.p.z + q.z;
      return m.mapPoint(p, this.world.bodyPosFn, out);
    }
    if (anchorBody) return m.mapRelative(anchorBody.mapped, anchorBody.p, this.anchor.q, out);
    return m.mapGlobal(this.anchor.q, out);
  }

  private readonly frame = (): void => {
    const now = performance.now();
    const dt = Math.min(0.25, (now - this.lastTime) / 1000);
    this.lastTime = now;

    advancePlayback(dt);
    this.updateIntroHold(dt);
    // one texture upload per frame at most (S3-02)
    this.bodies.uploadNext(this.renderer);
    const pb = get(playback);
    const playing = pb.playing;
    const daysPerS = pb.playing ? ((pb.reverse ? -1 : 1) * effectiveSpeed()) / 86_400 : 0;
    this.bodies.setPlaybackSpeed(daysPerS);
    this.sprites.setPlaybackSpeed(daysPerS);
    if (!Number.isNaN(this.introTime)) {
      this.introTime += dt;
      this.bodies.setIgnition(Math.max(0, this.introTime) / INTRO_IGNITION_S);
      if (this.introTime > INTRO_SWEEP_S) {
        this.introTime = Number.NaN;
        // the intro's start-up frames say nothing about the device: measure from here
        this.qualityMonitor.reset();
      }
    }
    this.bodies.animate(this.motionReduced ? 0 : now / 1000);
    // no automatic tier change during the intro: the switch itself would put a hitch into the camera sweep
    // … nor for a frame that precomputed trail samples: that work ends, the device stays as fast (S4-15)
    if (this.qualityAuto && !this.introHold && Number.isNaN(this.introTime) && !precomputedThisFrame()) {
      const dropped = this.qualityMonitor.frame(dt);
      if (dropped) this.applyQuality(dropped);
    }
    this.updateViewOffset(dt);
    const profileChanged = this.updateProfile(dt);
    // the world only changes with the date, the profile or new data — not while the camera alone moves (S4-15: a
    // touch drag recomputed every body, moon theory and spacecraft each frame)
    const tdb = utcMsToTdbDays(this.dateMs);
    if (tdb !== this.worldTdb || this.profileVersion !== this.worldProfile || this.worldDirty) {
      this.world.update(tdb);
      this.worldTdb = tdb;
      this.worldProfile = this.profileVersion;
      this.worldDirty = false;
    }

    if (this.pendingFlight) {
      this.pendingFlight = false;
      const sc = this.world.spacecraftById.get(this.focusId);
      this.anchor = null;
      if (sc && this.followId !== this.focusId) this.anchorAtSpacecraft(this.focusId);
      this.resolveFocus(this.tmp);
      this.rig.flyTo(this.tmp, this.framingDistance(this.focusId), this.motionReduced || !this.started);
      this.lastRefLength = this.referenceLength();
    }

    if (this.introSweepPending) {
      this.introSweepPending = false;
      // held at the far start position until the hold ends (releaseHold), at most INTRO_HOLD_MAX_S
      this.rig.sweep(INTRO_SWEEP_S, 7, Math.PI * 0.6, INTRO_HOLD_MAX_S);
    }

    const ref = this.referenceLength();
    if (profileChanged && Number.isFinite(this.lastRefLength) && this.lastRefLength > 0 && ref > 0) {
      this.rig.scaleDistance(ref / this.lastRefLength);
    }
    this.lastRefLength = ref;

    const body = this.world.bodyById.get(this.focusId);
    this.rig.minFocusDistance = body ? 1.2 * body.r : this.landedClearance();
    this.updateCinematics(dt);
    this.rig.update(dt, this.resolveFocus(this.focusPoint), !this.motionReduced);
    const origin = this.rig.origin;

    const view = this.viewInfo();
    const minPx = this.profile.spriteMinPx;
    this.bodies.update(origin, this.profileVersion, minPx, view);
    const closeId = this.followId ?? (this.world.spacecraftById.has(this.focusId) ? this.focusId : null);
    this.sprites.update(
      origin,
      minPx + 8,
      view.pxPerDuAtUnit,
      closeId,
      this.camera,
      this.world.bodyById.get('earth')?.d ?? null,
      Math.min(this.width, this.height) < 500,
    );
    const markerScale = (minPx + 6) / view.pxPerDuAtUnit;
    // the next flyby beacon breathes once per 1.6 s (no motion with prefers-reduced-motion)
    const pulse = this.motionReduced ? 0 : 0.5 + 0.5 * Math.sin((now / 1600) * 2 * Math.PI);
    // during the sweep the lines of the start are complete (the hold waits for them); anything else gets less time
    const sweeping = !Number.isNaN(this.introTime);
    // trail sampling yields during the intro sweep and while a finger or the mouse moves the camera, and on phones
    // while time plays or the camera flies (S4-15)
    const moving = playing || this.rig.flying || this.pendingFlight || this.mix !== this.mixTarget;
    beginFrame(FRAME_SAMPLE_BUDGET_MS);
    for (const line of this.lines) {
      line.update(origin, this.profileVersion, this.settings.lineFrame, markerScale, pulse, this.predictedFrom);
    }
    this.betweenFramesBudgetMs =
      sweeping || this.pointers.size > 0
        ? 0
        : moving
          ? (this.sampleBudgetMs ?? MOVING_SAMPLE_BUDGET_MS)
          : REST_SAMPLE_BUDGET_MS;
    if (this.betweenFramesBudgetMs > 0 && !this.betweenFramesQueued && this.lines.some((l) => l.pending)) {
      this.betweenFramesQueued = true;
      setTimeout(this.sampleBetweenFrames, 0);
    }

    // no label updates during the sweep: they would only shuffle around while the system grows (and cost DOM work
    // per frame); they are placed in the dark hold already (invisible), so the first placement does not hitch later.
    // Nor in the very first frame: their first placement and layout go to the second (S4-03 part 6: together the
    // first frame was a long task at 4× CPU)
    const labels = !sweeping && this.started;
    if (labels) this.updateLabels(view);
    this.updateContext();
    this.started = true;

    this.starCamera.quaternion.copy(this.camera.quaternion);
    this.renderer.info.reset();
    this.postfx.render(dt);
    if (labels) this.labelRenderer.render(this.scene, this.camera);
    if (!this.firstFrameMeasured) {
      this.firstFrameMeasured = true;
      performance.measure('zeitflug:first-frame', { start: now });
      // the post-processing chain and the HDR variants: soon if the start tier needs them, else for later changes
      const delay = this.requestedTier === 'low' ? HDR_DELAY_LOW_S : HDR_DELAY_S;
      this.warmupTimer = setTimeout(() => void this.prepareHdr(), delay * 1000);
    }

    // CPU time of this frame (JS + command submission), exponential average for the perf report (S1-15)
    const cpu = performance.now() - now;
    this.cpuMs = this.cpuMs === 0 ? cpu : this.cpuMs * 0.95 + cpu * 0.05;
    this.cpuMaxMs = Math.max(this.cpuMaxMs, cpu);
    this.cpuSamples[this.cpuCount++ % this.cpuSamples.length] = cpu;

    this.fpsFrames++;
    this.fpsTime += dt;
    if (this.fpsTime >= 0.5) {
      fpsStore.set(this.fpsFrames / this.fpsTime);
      this.fpsFrames = 0;
      this.fpsTime = 0;
    }
  };

  /**
   * Whether the bubble of `bodyId` is open enough on screen (≥ MIN_BUBBLE_PICK_PX) for the objects inside it to be
   * picked and labelled (progressive disclosure). No body = always open.
   */
  private bubbleOpen(bodyId: string | null | undefined, view: ViewInfo): boolean {
    if (!bodyId) return true;
    const v = this.bodies.visuals.find((x) => x.body.data.id === bodyId);
    if (!v) return true;
    const dist = Math.max(v.group.position.distanceTo(this.camera.position), 1e-12);
    return (this.world.displayBubbleRadius(bodyId) / dist) * view.pxPerDuAtUnit >= MIN_BUBBLE_PICK_PX;
  }

  private updateLabels(view: ViewInfo): void {
    let focusDisc: { x: number; y: number; r: number } | null = null;
    const focusedBody = this.world.bodyById.get(this.focusId);
    if (focusedBody) {
      const r = this.bodies.projectedRadiusPx(this.focusId, view);
      // the focus sits at the origin; with a view offset (HUD insets) that is not the canvas centre
      const c = this.tmpV3.set(0, 0, 0).project(this.camera);
      if (r > 6) focusDisc = { x: ((c.x + 1) / 2) * this.width, y: ((1 - c.y) / 2) * this.height, r: r + 4 };
    }
    for (const label of this.labels) {
      const body = this.world.bodyById.get(label.id);
      if (body) {
        label.offsetPx = Math.max(this.bodies.projectedRadiusPx(label.id, view), this.profile.spriteMinPx / 2) + 4;
        // moons are labelled only once their planet's bubble is open, like picking, and not while blurred into
        // their orbit ring at fast playback
        label.wanted = body.active && this.bubbleOpen(body.parent?.data.id, view) && this.bodies.blurOf(label.id) < 0.5;
      } else if (this.siteNames.has(label.id)) {
        // the site's name once the spacecraft has left it (on the surface the spacecraft's own label is there)
        const sc = this.world.spacecraftById.get(label.id.slice(0, -SITE_SUFFIX.length));
        label.wanted = !!sc?.endVisible && sc.lifeState !== 'landed' && this.bubbleOpen(sc.landing?.body ?? null, view);
        label.offsetPx = 10;
      } else {
        const sc = this.world.spacecraftById.get(label.id);
        label.wanted = sc?.visible ?? false;
        if (label.wanted) this.spacecraftLabelText(label);
        label.offsetPx = (this.profile.spriteMinPx + 8) / 2 + 4;
      }
      // declutter priority (S3-12): focused > followed > the selected mission's spacecraft > the rest
      const sc = this.world.spacecraftById.get(label.id);
      label.priority =
        label.id === this.focusId
          ? 200
          : label.id === this.followId
            ? 190
            : sc && sc.missionId === this.selectedMission
              ? 150
              : label.basePriority;
    }
    declutter(this.labels, this.camera, this.width, this.height, focusDisc);
  }

  private updateContext(): void {
    const sc = this.world.spacecraftById.get(this.focusId);
    const ctx = sc ? sc.bubble : null;
    if (ctx !== this.lastContext) {
      this.lastContext = ctx;
      focusContext.set(ctx);
    }
  }

  // ── debug & tests ────────────────────────────────────────────────────────

  /** Re-applies settings after the debug panel changed them. */
  settingsChanged(): void {
    this.profileKey = '';
  }

  /** Snapshot for tests and the spike report (exposed as window.__zeitflug in debug builds). */
  debugState(): Record<string, unknown> {
    return {
      date: new Date(this.dateMs).toISOString(),
      quality: this.postfx.quality,
      qualityAuto: this.qualityAuto,
      focus: this.focusId,
      follow: this.followId,
      mix: this.mix,
      cameraDistance: this.rig.distance,
      flying: this.pendingFlight || this.rig.flying,
      cpuMsPerFrame: this.cpuMs,
      cpuMsPeak: this.cpuMaxMs,
      drawCalls: this.renderer.info.render.calls,
      triangles: this.renderer.info.render.triangles,
      dpr: this.dpr,
      // the fleet (S3-12): every spacecraft with its life cycle, and what the loader still has to fetch (S3-03)
      spacecraft: this.world.spacecraft.map((sc) => ({
        id: sc.id,
        loaded: !!sc.trajectory || !!sc.symbolic,
        visible: sc.visible,
        state: sc.lifeState,
        frame: sc.state.frame,
        bubble: sc.bubble,
        endMarker: sc.endVisible,
        trail: this.lineById.get(sc.id)?.ready ?? false,
      })),
      loaderQueue: this.loader?.queue() ?? [],
      introBench: this.introBench,
      selectedMission: this.selectedMission,
    };
  }

  /** Sets the date through the store action (deterministic stepping in measurement scripts). */
  debugSetDate(ms: number): void {
    setDate(ms);
  }

  /** Starts a new measurement window for cpuMsPerFrame / cpuMsPeak. */
  resetPerfStats(): void {
    this.cpuMs = 0;
    this.cpuMaxMs = 0;
    this.cpuCount = 0;
  }

  /** CPU time of each frame since resetPerfStats (the last 1,024 at most), ms — for the perf scenarios. */
  debugCpuSamples(): number[] {
    return Array.from(this.cpuSamples.subarray(0, Math.min(this.cpuCount, this.cpuSamples.length)));
  }

  /**
   * Live telemetry of a spacecraft at the current date, written into `out` (S2-04). Returns the id of the body whose
   * bubble it is in ('sun' outside every bubble), or null when the spacecraft is not active at this date.
   */
  telemetry(id: string, out: Telemetry): string | null {
    const sc = this.world.spacecraftById.get(id);
    const earth = this.world.bodyById.get('earth');
    if (!sc?.visible || !earth || Number.isNaN(this.world.tdb)) return null;
    const near = sc.bubble ? this.world.bodyById.get(sc.bubble) : undefined;
    const inFrame = near && sc.state.frame === near.data.id;
    computeTelemetry(
      out,
      sc.state.p,
      sc.state.v,
      earth.p,
      near?.p ?? null,
      inFrame ? sc.state.w : null,
      sc.launchMs,
      this.dateMs,
    );
    return near?.data.id ?? 'sun';
  }

  /**
   * Renders one frame and returns it as a PNG (photo mode, S2-20). The canvas is read right after rendering, while
   * the drawing buffer is still valid (no preserveDrawingBuffer needed).
   */
  capture(): Promise<Blob | null> {
    this.renderer.info.reset();
    this.postfx.render(0);
    return new Promise((resolve) => this.renderer.domElement.toBlob(resolve, 'image/png'));
  }

  /** Distance (km) between a spacecraft and a body at `utcMs`, or null outside the trajectory (title cards, S2-10). */
  distanceAt(scId: string, bodyId: string, utcMs: number): number | null {
    const sc = this.world.spacecraftById.get(scId);
    if (!sc) return null;
    const t = utcMsToTdbDays(utcMs);
    const bodyAt = (id: string, tt: number): Vec3 => this.world.bodyAt(id, tt);
    if (!sc.trajectory?.stateAt(t, bodyAt, this.probeState)) return null;
    const b = this.world.bodyAt(bodyId, t);
    const p = this.probeState.p;
    return Math.hypot(p.x - b.x, p.y - b.y, p.z - b.z);
  }

  /** What a tap at canvas position (x, y) would pick, with the candidates (tests and debugging). */
  pickAt(
    x: number,
    y: number,
  ): { id: string | null; candidates: { id: string; radiusPx: number; inside?: readonly string[] }[] } {
    const list = this.pickables();
    return {
      id: pick(list, this.camera, x, y, this.width, this.height),
      candidates: list.map((p) => ({ id: p.id, radiusPx: p.radiusPx, ...(p.inside ? { inside: p.inside } : {}) })),
    };
  }

  /** Projected screen position of an object (for Playwright tests). */
  screenPosition(id: string): { x: number; y: number } | null {
    // `<spacecraft>:site`: the landing site marker (S4-11)
    const site = id.endsWith(SITE_SUFFIX) ? id.slice(0, -SITE_SUFFIX.length) : null;
    const obj = site
      ? this.sprites.visuals.find((v) => v.sc.id === site)?.endMarker
      : (this.bodies.visuals.find((v) => v.body.data.id === id)?.group ??
        this.sprites.visuals.find((v) => v.sc.id === id)?.sprite);
    if (!obj?.visible || !this.started) return null;
    const p = obj.position.clone().project(this.camera);
    if (!Number.isFinite(p.x) || !Number.isFinite(p.y)) return null;
    return { x: ((p.x + 1) / 2) * this.width, y: ((1 - p.y) / 2) * this.height };
  }
}
